import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, ChevronDown } from "lucide-react";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import StatsBand from "../components/StatsBand";
import CTASection from "../components/CTASection";
import useSeo from "../lib/useSeo";
import { IMAGES, CORE_VALUES, SERVICES, WHY_CHOOSE } from "../lib/data";

const HEADLINE_LINES = [
  "Marine Logistics",
  "& Manpower Services",
];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  useSeo(
    "Mauresk Services — Marine Logistics & Manpower for Oil & Gas",
    "UAE-based marine logistics, ship management, vessel chartering and offshore manpower services supporting the global Oil & Gas industry."
  );

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <>
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative isolate flex min-h-screen items-center overflow-hidden bg-navy pb-20 pt-32"
      >
        <motion.img
          src={IMAGES.hero}
          alt="Offshore oil platform standing in the open ocean"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          style={{ y: imgY, scale: imgScale }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-navy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/40" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-8">
          <h1 className="font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[5.5rem]">
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className="block"
                  variants={lineVariant}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                >
                  {line}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                variants={lineVariant}
                custom={2}
                initial="hidden"
                animate="visible"
              >
                in Ajman Offshore Zone
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-white/75"
          >
            Supporting the global Oil & Gas industry with reliable Marine
            Logistics, Ship Management, Vessel Chartering and highly qualified
            offshore professionals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="/quote"
              data-testid="hero-request-quote"
              className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-brandred px-8 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-brandred-600"
            >
              Request a Quote <ArrowUpRight className="h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-us"
              className="inline-flex items-center justify-center gap-1.5 rounded-sm border border-white/25 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white hover:text-navy"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/50"
        >
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </motion.div>
      </section>

      {/* ABOUT PREVIEW */}
      <section data-testid="about-preview" className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-brandred">
                  <span className="h-px w-10 bg-brandred" /> About Mauresk
                </p>
                <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl">
                  A UAE-based partner built for offshore reliability.
                </h2>
                <p className="mt-6 text-lg leading-relaxed text-[#4B5563]">
                  Mauresk Services specialises in Marine Logistics & Manpower
                  Services supporting the Oil & Gas industry. With more than 15
                  years of experience, we deliver reliable offshore solutions
                  while maintaining the highest standards of safety, quality and
                  professionalism.
                </p>
                <Link
                  to="/about"
                  data-testid="about-preview-link"
                  className="mt-8 inline-flex items-center gap-2 text-base font-semibold text-navy transition-colors duration-200 hover:text-brandred"
                >
                  Discover our story <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={1}>
                <div className="relative">
                  <img
                    src={IMAGES.aboutCard}
                    alt="Offshore oil and gas production platform with gas flare at sea"
                    loading="lazy"
                    className="aspect-[4/5] w-full rounded-sm object-cover shadow-[0_20px_60px_rgb(10,25,47,0.15)]"
                  />
                  <div className="absolute -bottom-6 -left-6 hidden w-48 rounded-sm bg-navy p-6 text-white sm:block">
                    <div className="font-display text-4xl font-extrabold">15+</div>
                    <div className="mt-1 text-sm text-white/60">
                      Years of offshore expertise
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-navy/10 bg-lightgray py-6">
        <Marquee speed={40} gradient={false} autoFill>
          {[
            "Marine Logistics",
            "Ship Chartering",
            "Crew Management",
            "ISM · ISPS Compliance",
            "Offshore Manpower",
            "Vessel Operations",
            "HSE Excellence",
          ].map((t) => (
            <span
              key={t}
              className="mx-8 font-display text-2xl font-semibold uppercase tracking-tight text-navy/70"
            >
              {t}
              <span className="ml-8 text-brandred">/</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* SERVICES PREVIEW */}
      <section data-testid="services-preview" className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-brandred">
              <span className="h-px w-10 bg-brandred" /> What We Do
            </p>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl">
              Integrated offshore services, end to end.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i % 2}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.slug}`}
                  className="group relative block overflow-hidden rounded-sm border border-navy/10 bg-white transition-transform duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgb(10,25,47,0.12)]"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-navy/30" />
                    <div className="absolute left-5 top-5 flex h-12 w-12 items-center justify-center rounded-sm bg-white/95 text-navy">
                      <Icon name={s.icon} className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="p-7">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-2xl font-bold text-navy">
                        {s.title}
                      </h3>
                      <ArrowUpRight className="h-6 w-6 text-navy/30 transition-colors duration-200 group-hover:text-brandred" />
                    </div>
                    <p className="mt-3 text-[#4B5563]">{s.short}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section
        data-testid="core-values-home"
        className="bg-lightgray py-24 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-brandred">
              <span className="h-px w-10 bg-brandred" /> Core Values
            </p>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl">
              The principles behind every operation.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CORE_VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i % 3}>
                <div className="group h-full rounded-sm border border-navy/10 bg-white p-8 transition-transform duration-300 hover:-translate-y-2 hover:shadow-[0_18px_40px_rgb(10,25,47,0.1)]">
                  <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-navy text-white transition-colors duration-300 group-hover:bg-brandred">
                    <Icon name={v.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold text-navy">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-[#4B5563]">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE PREVIEW */}
      <section data-testid="why-preview" className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-brandred">
                  <span className="h-px w-10 bg-brandred" /> Why Choose Us
                </p>
                <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl">
                  Trusted where it matters most.
                </h2>
                <p className="mt-6 text-lg text-[#4B5563]">
                  From rapid mobilisation to international safety standards, we
                  built our reputation on delivering offshore excellence.
                </p>
                <Link
                  to="/why-choose-us"
                  className="mt-8 inline-flex items-center gap-2 text-base font-semibold text-navy transition-colors duration-200 hover:text-brandred"
                >
                  See all reasons <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="grid gap-px overflow-hidden rounded-sm border border-navy/10 bg-navy/10 sm:grid-cols-2">
                {WHY_CHOOSE.slice(0, 4).map((w, i) => (
                  <Reveal key={w.title} delay={i % 2}>
                    <div className="h-full bg-white p-8">
                      <Icon name={w.icon} className="h-7 w-7 text-brandred" />
                      <h3 className="mt-5 font-display text-lg font-bold text-navy">
                        {w.title}
                      </h3>
                      <p className="mt-2 text-sm text-[#4B5563]">{w.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <StatsBand />
      <CTASection />
    </>
  );
}
